/* eslint-disable max-lines */
import {
  useAlarms,
  useAlarmTypeColorMappings,
  useGetAlarmNotificationPreference,
} from '@hakimo-ui/hakimo/data-access';
import { AlarmsDTO } from '@hakimo-ui/hakimo/types';
import { FilterPanel } from '@hakimo-ui/hakimo/ui-filters';
import { Page } from '@hakimo-ui/hakimo/ui-layout';
import { TableData } from '@hakimo-ui/hakimo/ui-table';
import {
  audioNotifyAtom,
  currentTenantAtom,
  systemNotifyAtom,
  trackFilters,
  useCanViewTenantColumn,
  useLocalStorage,
  useUser,
} from '@hakimo-ui/hakimo/util';
import { Alert } from '@hakimo-ui/shared/ui-base';
import { useAtom, useAtomValue } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useOnClickRow } from '../shared/hooks/useOnClickRow';
import { notifyNewAlarm } from '../shared/util/common';
import { getSearchParams } from '../shared/util/request-util';
import { useFilterOverride } from '../shared/util/useFilterOverride';
import AlarmListTable from './alarm-list-table/AlarmListTable';
import { ALARM_QUERY_INTERVAL, PAGE_SIZE } from './constants';
import {
  defaultFilterValues,
  getFilterConfigs,
  getResetDefaultFilterValues,
  patchFilterValues,
} from './filter-utils';
import { pageAtom } from './state';
import { FilterValues } from './types';
import { useUpdateRenderedTime } from './useUpdateRenderedTime';
import {
  applyAutoFilters,
  columns,
  getTableData,
  isAutoAppliedFilter,
} from './util';
function AlarmList() {
  const user = useUser();
  const currentTenant = useAtomValue(currentTenantAtom);
  const tenants = useMemo(
    () => (currentTenant ? [currentTenant] : []),
    [currentTenant]
  );
  // Per-tenant routing: the tenant's own dataset, independent of the manual
  // override useApiDataVersion reads. Only v3 reaches the wire either way.
  const dataVersion = currentTenant?.version;
  const canViewTenantColumn = useCanViewTenantColumn();
  const [filterValuesAtom] = useState(() =>
    atomWithStorage(
      'rawAlarms/filters',
      defaultFilterValues(canViewTenantColumn, user)
    )
  );
  const [page, setPage] = useAtom(pageAtom);
  const [filterValues, setFilterValues] = useAtom(filterValuesAtom);
  const [total, setTotal] = useState(0);
  const [queryParams, setQueryParams] = useState<string>(
    getSearchParams(page, PAGE_SIZE, filterValues, tenants)
  );
  const audioNotify = useAtomValue(audioNotifyAtom);
  const [openFilterPanel, setOpenFilterPanel] = useState(false);
  const filterConfigs = getFilterConfigs(canViewTenantColumn, user);
  const systemNotifyPreference = useAtomValue(systemNotifyAtom);

  const availableColumnIds = useMemo(
    () =>
      columns
        .filter((col) => col.id !== 'tenant' || canViewTenantColumn)
        .map((col) => col.id),
    [canViewTenantColumn]
  );
  const [tableData, setTableData] = useState<TableData>({
    columns,
    rows: [],
  });
  const prevData = useRef<AlarmsDTO>();
  const [shownColumns, setShownColumns] = useLocalStorage<string[]>(
    'alarm-list-shown-columns',
    availableColumnIds
  );

  const [hasManualOverride, setHasManualOverride] = useLocalStorage(
    'rawAlarms/hasManualTypeOverride',
    false
  );
  useFilterOverride(setHasManualOverride);

  useEffect(() => {
    setFilterValues((values) => {
      const patchedValues = patchFilterValues(
        values,
        canViewTenantColumn,
        user
      );
      return applyAutoFilters(patchedValues, user, hasManualOverride || false);
    });
  }, [
    setFilterValues,
    canViewTenantColumn,
    user?.alarm_types,
    user?.default_alarm_status_list,
    hasManualOverride,
  ]);

  useEffect(() => {
    setQueryParams(getSearchParams(page, PAGE_SIZE, filterValues, tenants));
  }, [page, filterValues, tenants]);

  const { isLoading, isError, error, data, isRefetching } = useAlarms(
    queryParams,
    ALARM_QUERY_INTERVAL,
    undefined,
    false,
    false,
    dataVersion
  );

  const onClickRow = useOnClickRow(dataVersion);
  useUpdateRenderedTime(data);
  const { data: notificationData } = useGetAlarmNotificationPreference();
  const { data: colorMappingsData } = useAlarmTypeColorMappings();

  useEffect(() => {
    if (data) {
      const { items, total: t } = data;
      const colorMappings = colorMappingsData?.payload || [];
      const newData = getTableData(
        items,
        onClickRow,
        canViewTenantColumn,
        shownColumns ?? availableColumnIds,
        colorMappings
      );
      notifyNewAlarm(
        prevData.current?.items,
        data.items,
        page,
        'alarm',
        audioNotify,
        systemNotifyPreference,
        notificationData
      );
      prevData.current = data;
      setTableData(newData);
      setTotal(t);
    }
  }, [
    data,
    onClickRow,
    canViewTenantColumn,
    shownColumns,
    availableColumnIds,
    page,
    audioNotify,
    systemNotifyPreference,
    notificationData,
    colorMappingsData,
  ]);

  const onApplyFilters = (values: FilterValues) => {
    setFilterValues(values);
    trackFilters(values);
    setOpenFilterPanel(false);
    setPage(1);
    if (!isAutoAppliedFilter(values, user)) {
      setHasManualOverride(true);
    }
  };

  const onResetFilters = () => {
    const resetValues = getResetDefaultFilterValues(canViewTenantColumn, user);
    setFilterValues(resetValues);
  };

  return (
    <Page title="Alarms" titleFontSize="large">
      {isError && (
        <div className="mb-2">
          <Alert type="error">{error.message}</Alert>
        </div>
      )}
      <FilterPanel
        title="Alarm Filters"
        open={openFilterPanel}
        filterConfigs={filterConfigs}
        initialFilterValues={filterValues}
        trackingEventNameApply="apply_alarms_filters"
        trackingEventNameReset="reset_alarms_filters"
        onClose={() => setOpenFilterPanel(false)}
        onApply={onApplyFilters}
        onReset={onResetFilters}
        enableNegativeFilters
      />
      <AlarmListTable
        tableData={tableData}
        canViewTenantColumn={canViewTenantColumn}
        shownColumns={shownColumns ?? availableColumnIds}
        isLoading={(isLoading || isRefetching) && !tableData}
        isRefetching={isRefetching || isLoading}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        filterValues={filterValues}
        tenants={tenants}
        onChangeShownColumns={setShownColumns}
        onOpenFilterPanel={() => setOpenFilterPanel(true)}
        onChangePage={(val) => setPage(val)}
      />
    </Page>
  );
}
export default AlarmList;
